import React, { useEffect, useState } from "react";
import { fetchTeam, fetchTeamMeta, createTeamMember, updateTeamMember, deleteTeamMember } from "../services/api";

// Admin — internal team & access ("bundles"). Add KODIS/ODRA team members and
// grant a role (a bundle of capabilities), plus optional extra capabilities.
// Gated to team.manage on the backend. English-only (internal tool).
const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const inp = { padding: 9, borderRadius: 8, border: "1px solid #d8dee2", fontSize: 14 };
const ROLE_LABEL = { super_admin: "Super Admin", technical_lead: "Technical Lead", support_agent: "Support Agent", sales: "Sales" };

export default function AdminTeamsPage() {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ roles: {}, capabilities: [] });
  const [form, setForm] = useState({ email: "", name: "", role: "support_agent" });
  const [err, setErr] = useState("");
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({ role: "", active: true, extra_caps: [] });

  const load = () => fetchTeam().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); fetchTeamMeta().then(setMeta).catch(() => {}); }, []);

  const add = async () => {
    setErr("");
    if (!form.email.trim()) { setErr("Email is required."); return; }
    try { await createTeamMember({ email: form.email.trim(), name: form.name.trim() || null, role: form.role }); setForm({ email: "", name: "", role: "support_agent" }); load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not add member."); }
  };
  const startEdit = (m) => { setEditId(m.id); setEdit({ role: m.role, active: m.active, extra_caps: m.extra_caps || [] }); };
  const saveEdit = async (id) => {
    try { await updateTeamMember(id, { email: rows.find((x) => x.id === id).email, role: edit.role, active: edit.active, extra_caps: edit.extra_caps }); setEditId(null); load(); }
    catch { /* */ }
  };
  const toggleCap = (c) => setEdit((e) => ({ ...e, extra_caps: e.extra_caps.includes(c) ? e.extra_caps.filter((x) => x !== c) : [...e.extra_caps, c] }));
  const remove = async (m) => { if (!window.confirm(`Deactivate ${m.email}?`)) return; try { await deleteTeamMember(m.id); load(); } catch { /* */ } };

  const rolesList = Object.keys(meta.roles || {});

  return (
    <div style={{ maxWidth: 940, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 2 }}>Team & access</h1>
      <div className="muted" style={{ fontSize: 14 }}>Add internal team members and grant access by role. A role is a bundle of capabilities; you can add extra capabilities per person.</div>

      {/* add member */}
      <div style={{ ...CARD, padding: 16, marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input style={{ ...inp, width: 220 }} placeholder="Email (their ODRA login)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input style={{ ...inp, width: 160 }} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <select style={inp} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
          {rolesList.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
        </select>
        <button className="btn primary small" onClick={add}>Add member</button>
        {err && <span style={{ color: "#A32D2D", fontSize: 13, width: "100%" }}>{err}</span>}
      </div>

      {/* role bundle reference */}
      <div style={{ ...CARD, padding: 14, marginTop: 12 }}>
        <div style={{ fontWeight: 700, color: "#063b48", marginBottom: 6, fontSize: 14 }}>Role bundles</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 10 }}>
          {rolesList.map((r) => (
            <div key={r} style={{ border: "1px solid #eef2f2", borderRadius: 10, padding: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{ROLE_LABEL[r] || r}</div>
              <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>{r === "super_admin" ? "Everything" : (meta.roles[r] || []).join(", ")}</div>
            </div>
          ))}
        </div>
      </div>

      {/* members */}
      <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
        {rows == null ? <p className="muted">…</p> : rows.length === 0 ? (
          <div style={{ ...CARD, padding: 20, textAlign: "center" }} className="muted">No team members yet.</div>
        ) : rows.map((m) => (
          <div key={m.id} style={{ ...CARD, padding: "12px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{m.name || m.email} {!m.active && <span style={{ color: "#A32D2D", fontSize: 12 }}>· inactive</span>}</div>
                <div className="muted" style={{ fontSize: 13 }}>{m.email} · {ROLE_LABEL[m.role] || m.role}{m.extra_caps.length ? ` · +${m.extra_caps.join(", ")}` : ""}</div>
              </div>
              {editId === m.id
                ? <button className="btn primary small" onClick={() => saveEdit(m.id)}>Save</button>
                : <button className="btn secondary small" onClick={() => startEdit(m)}>Edit</button>}
              <button onClick={() => remove(m)} style={{ fontSize: 13, background: "none", border: "none", color: "#A32D2D", cursor: "pointer" }}>Deactivate</button>
            </div>
            {editId === m.id && (
              <div style={{ marginTop: 10, borderTop: "1px solid #eef2f2", paddingTop: 10 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <label style={{ fontSize: 13 }}>Role
                    <select style={{ ...inp, marginLeft: 6 }} value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })}>
                      {rolesList.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
                    </select>
                  </label>
                  <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} /> Active
                  </label>
                </div>
                <div style={{ fontSize: 12.5, color: "#5b6472", margin: "10px 0 4px" }}>Extra capabilities (beyond the role)</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {(meta.capabilities || []).map((c) => (
                    <label key={c} style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4, border: "1px solid #e5e9ec", borderRadius: 8, padding: "3px 8px" }}>
                      <input type="checkbox" checked={edit.extra_caps.includes(c)} onChange={() => toggleCap(c)} /> {c}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
